import LegalLayout, { Section } from "@/components/LegalLayout";

const LAST_UPDATED = "27 February 2026";
const PREFIX = "cookies";

export default function CookiePolicyPage() {
  return (
    <LegalLayout
      title="Cookie Policy"
      canonical="https://transportanything247.com/cookies"
      description="How Transport Anything 247 uses cookies, local storage and similar technologies on the website and mobile apps."
      lastUpdated={LAST_UPDATED}
      testidPrefix={PREFIX}
    >
      <Section id="overview" title="1. Overview" testidPrefix={PREFIX}>
        <p>
          This Cookie Policy explains how Transport Anything 247 ("we", "us", "our") uses cookies and
          similar technologies on the transportanything247.com website and the Transport Anything 247
          and Transport 247 Driver mobile apps (together, "the Service"). It complements our{" "}
          <a href="/privacy" className="text-orange-600 underline">Privacy Policy</a>.
        </p>
        <p>
          The UK GDPR and the Privacy and Electronic Communications Regulations 2003 (PECR) require us
          to obtain your consent before we set non-essential cookies. We do not currently use any
          non-essential cookies — see Section 3.
        </p>
      </Section>

      <Section id="what-are-cookies" title="2. What are cookies and local storage" testidPrefix={PREFIX}>
        <p>
          Cookies are small text files placed on your device by websites you visit. Local storage and
          IndexedDB are larger client-side stores used by modern web apps. Mobile apps additionally use
          the platform's secure storage (iOS Keychain, Android Keystore) and the device's filesystem.
        </p>
        <p>
          For the purposes of this Policy, "cookies" refers to all of the above mechanisms.
        </p>
      </Section>

      <Section id="cookies-we-use" title="3. Cookies we use" testidPrefix={PREFIX}>
        <p>
          We use only <strong>strictly necessary</strong> cookies and local storage entries — those
          required to deliver the core functionality of the Service. We do <strong>not</strong> use
          advertising, tracking or third-party analytics cookies.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border border-slate-200 rounded-lg">
            <thead className="bg-slate-100 text-slate-700">
              <tr>
                <th className="text-left p-2 border-b border-slate-200">Item</th>
                <th className="text-left p-2 border-b border-slate-200">Type</th>
                <th className="text-left p-2 border-b border-slate-200">Purpose</th>
                <th className="text-left p-2 border-b border-slate-200">Lifetime</th>
              </tr>
            </thead>
            <tbody className="text-slate-700">
              <tr>
                <td className="p-2 font-mono">driver_token, customer_token</td>
                <td className="p-2">localStorage</td>
                <td className="p-2">Authentication — keeps you signed in (JWT).</td>
                <td className="p-2">Until logout or token expiry</td>
              </tr>
              <tr className="bg-slate-50">
                <td className="p-2 font-mono">driver_home_coords_v1</td>
                <td className="p-2">localStorage</td>
                <td className="p-2">Stores driver's chosen "home" coordinates so we can surface return-load matches on the route home.</td>
                <td className="p-2">Until reset by driver</td>
              </tr>
              <tr>
                <td className="p-2 font-mono">driver_perf_widget_opt_in_v1</td>
                <td className="p-2">localStorage</td>
                <td className="p-2">Stores whether the driver has dismissed their private performance widget.</td>
                <td className="p-2">Until reset by driver</td>
              </tr>
              <tr className="bg-slate-50">
                <td className="p-2 font-mono">ta247_pwa_install_dismissed_v1</td>
                <td className="p-2">localStorage</td>
                <td className="p-2">Remembers that you've dismissed the "Install Transport 247" home-screen prompt.</td>
                <td className="p-2">Until cleared</td>
              </tr>
              <tr>
                <td className="p-2 font-mono">ta247-pod-queue</td>
                <td className="p-2">IndexedDB</td>
                <td className="p-2">(Driver app only) Holds proof-of-delivery uploads while you are offline. Entries are deleted immediately on successful upload.</td>
                <td className="p-2">Per upload</td>
              </tr>
              <tr className="bg-slate-50">
                <td className="p-2 font-mono">Service-worker caches</td>
                <td className="p-2">Cache Storage</td>
                <td className="p-2">Caches the app's own static assets and HTML shell so the website loads faster and works on patchy connections. Never caches your data.</td>
                <td className="p-2">Refreshed on app updates</td>
              </tr>
              <tr>
                <td className="p-2 font-mono">Apple Keychain / Android Keystore (apps only)</td>
                <td className="p-2">Secure storage</td>
                <td className="p-2">Stores the JWT used by Face ID / fingerprint unlock. Your biometric data itself never leaves the device.</td>
                <td className="p-2">Until you sign out or disable biometric login</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Section>

      <Section id="no-third-party" title="4. No third-party or advertising cookies" testidPrefix={PREFIX}>
        <p>
          We do not place advertising cookies, social-media plug-in cookies, or third-party analytics
          trackers (such as Google Analytics, Facebook Pixel, Hotjar, or similar) on the Service. We
          do not allow third parties to set cookies through our site for cross-site tracking purposes.
        </p>
        <p>
          We rely on Stripe-hosted forms to handle card payments — your card data is entered on Stripe's
          domain and is subject to Stripe's own cookie practices. We embed OpenStreetMap tiles in the
          live-tracking view; the tile provider may set a session cookie for caching purposes.
        </p>
      </Section>

      <Section id="managing-cookies" title="5. Managing cookies" testidPrefix={PREFIX}>
        <p>
          Because we only use strictly necessary storage, you do not need to opt in. You can still
          control or clear our storage:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>In your browser, use Settings → Privacy / Clear browsing data.</li>
          <li>In the driver app, log out (clears the auth token + biometric vault).</li>
          <li>Uninstalling the app removes all stored data tied to it.</li>
        </ul>
        <p>
          Disabling our strictly necessary storage will make the Service unusable (you will be logged
          out and unable to access your account).
        </p>
      </Section>

      <Section id="changes" title="6. Changes to this Cookie Policy" testidPrefix={PREFIX}>
        <p>
          We will update this Policy if the storage we use changes. The "Last updated" date above
          shows when it was last revised.
        </p>
      </Section>

      <Section id="contact" title="7. Contact" testidPrefix={PREFIX}>
        <div className="bg-slate-900 text-white rounded-xl p-5">
          <p className="text-sm leading-relaxed">
            Questions about cookies or this Policy:<br />
            <a href="mailto:customers@transportanything247.com" className="text-orange-400 underline font-semibold">
              customers@transportanything247.com
            </a>
          </p>
        </div>
      </Section>
    </LegalLayout>
  );
}
